import type {
  AppSettings,
  BoxConfigBoxDetail,
  BoxConfigInput,
  BoxConfigSummary,
  CancelPlanInput,
  CancelVoidWindow,
  CreatePlanInput,
  DashboardSummary,
  DeliveryWindowOption,
  DeliveryWindowSelection,
  FbaInventoryItem,
  Incentive,
  LeadTimeSummary,
  ListPlansFilters,
  MeUser,
  PackingGroup,
  PackingOption,
  PlacementOption,
  PlanItem,
  PlanSummary,
  PrepCenter,
  PrepDetailsItem,
  Report,
  SetPlanItemInput,
  Shipment,
  Supplier,
  SupplierSku,
  TransportationOption,
  TransportationSelection,
  UserDto,
  UserRole,
  WorkflowStatusInfo
} from "../types.js";

/**
 * Cliente HTTP da API interna do FiCC (Cloud Run). Toda chamada autenticada
 * envia `Authorization: Bearer <idToken>` (o backend valida com firebase-admin,
 * ADR-011). O frontend fala EXCLUSIVAMENTE com `/api/v1/*` — nunca com a SP-API
 * nem qualquer serviço externo (invariante SDD §5 / ADR-002).
 *
 * `getToken`/`fetchImpl`/`baseUrl` são injetáveis: mantém o módulo livre de
 * Firebase (testável em node, sem Vite) e desacoplado do `import.meta.env`.
 */
export class ApiFetchError extends Error {
  readonly code: string;
  readonly status: number;
  /** Erros por campo do backend (Zod), ex.: `{ "boxes.0.weightKg": "..." }`. */
  readonly fields: Record<string, string> | null;

  constructor(
    message: string,
    code: string,
    status: number,
    fields: Record<string, string> | null = null
  ) {
    super(message);
    this.name = "ApiFetchError";
    this.code = code;
    this.status = status;
    this.fields = fields;
  }
}

export interface MeResponse {
  success: true;
  user: MeUser;
}

export interface PlansResponse {
  success: true;
  plans: PlanSummary[];
}

export interface PlanResponse {
  success: true;
  plan: PlanSummary;
}

export interface ShipmentsResponse {
  success: true;
  data: {
    inboundPlanId: string;
    shipments: Shipment[];
  };
}

export interface DashboardResponse {
  success: true;
  dashboard: DashboardSummary;
}

export interface LeadTimeResponse {
  success: true;
  leadTime: LeadTimeSummary;
}

// ───────────────────────── Wizard Criar envios (§6) ─────────────────────────
export interface ItemsResponse {
  success: true;
  data: { inboundPlanId: string; items: PlanItem[] };
}

export interface PrepDetailsResponse {
  success: true;
  data: { inboundPlanId: string; items: PrepDetailsItem[] };
}

export interface PrepDetailsConfirmResponse {
  success: true;
  data: { inboundPlanId: string; item: PrepDetailsItem };
}

export interface CancelPlanResponse {
  success: true;
  data: {
    inboundPlanId: string;
    status: "ENQUEUED";
    voidWindow: CancelVoidWindow;
  };
}

export interface EnqueuedResponse {
  success: true;
  data: { inboundPlanId: string; status: "ENQUEUED" };
}

export interface PackingOptionsResponse {
  success: true;
  data: { inboundPlanId: string; packingOptions: PackingOption[] };
}

/**
 * Grupos de embalagem da opção CONFIRMADA + os itens de cada grupo (relação
 * item↔grupo vinda da Amazon). A opção pode ter N grupos — cada um tem sua
 * própria planilha de caixas.
 */
export interface PackingGroupsResponse {
  success: true;
  data: {
    inboundPlanId: string;
    packingOptionId: string | null;
    groups: PackingGroup[];
  };
}

export interface PlacementOptionsResponse {
  success: true;
  data: {
    inboundPlanId: string;
    placementOptions: PlacementOption[];
    /**
     * Placement realmente em uso (seleção persistida). O workflow pode
     * re-selecionar outra ao procurar transportadora parceira — a UI mostra
     * esta, não a que o operador clicou. Opcional: ausente em API anterior.
     */
    selectedPlacementOptionId?: string | null;
  };
}

/**
 * Busca por transportadora PARCEIRA em andamento no backend: a oferta de
 * parceira depende da opção de posicionamento, então o workflow sonda as demais
 * placements antes de desistir. Enquanto `searching` for true a tela NÃO pode
 * concluir "a Amazon não ofereceu parceira" — tem de continuar pollando.
 * Opcional: ausente em API anterior a este contrato (fallback = não está
 * buscando, comportamento antigo).
 */
export interface PartneredSearch {
  searching: boolean;
  probed: number;
  total: number;
}

export interface TransportationOptionsResponse {
  success: true;
  data: {
    inboundPlanId: string;
    transportationOptions: TransportationOption[];
    partneredSearch?: PartneredSearch;
  };
}

export interface DeliveryWindowResponse {
  success: true;
  data: {
    inboundPlanId: string;
    shipmentId: string;
    deliveryWindowOptions: DeliveryWindowOption[];
  };
}

/** PUT /box-config retorna sem envelope `success` (apenas id + versão). */
export interface PutBoxConfigResponse {
  boxConfigId: string;
  version: number;
}

export interface BoxConfigDetailsResponse {
  success: true;
  data: {
    inboundPlanId: string;
    boxConfig: BoxConfigSummary | null;
    boxes: BoxConfigBoxDetail[];
  };
}

export interface WorkflowStartResponse {
  success: true;
  workflowInstanceId: string;
  workflowStatus: string;
}

export interface WorkflowStatusResponse extends WorkflowStatusInfo {
  success: true;
}

export interface WorkflowEventResponse {
  success: true;
  eventId: string;
}

export interface InventoryResponse {
  success: true;
  marketplaceId: string;
  items: FbaInventoryItem[];
}

export interface SyncInventoryResponse {
  success: true;
  status: "ENQUEUED";
  marketplaceId: string;
}

export interface FavoriteResponse {
  success: true;
  marketplaceId: string;
  sellerSku: string;
  favorite: boolean;
}

// ───────────────────────── Cadastro › Usuários (§7) ─────────────────────────
export interface UsersResponse {
  success: true;
  users: UserDto[];
  activeCount: number;
}

export interface UserResponse {
  success: true;
  user: UserDto;
}

export interface CreateUserInput {
  name: string;
  email: string;
  role: UserRole;
}

// ──────────────────────── Fase F — §8 (cadastros/relatório) ─────────────────
export interface SuppliersResponse {
  success: true;
  suppliers: Supplier[];
}

export interface SupplierResponse {
  success: true;
  supplier: Supplier;
}

export interface SupplierInput {
  name: string;
  address: string;
  leadTimeDays: number;
  skus: SupplierSku[];
}

export interface PrepCentersResponse {
  success: true;
  prepCenters: PrepCenter[];
}

export interface PrepCenterResponse {
  success: true;
  prepCenter: PrepCenter;
}

export interface PrepCenterInput {
  name: string;
  address: string;
  phone: string;
  skus: SupplierSku[];
}

export interface ReportsResponse {
  success: true;
  reports: Report[];
}

export interface ReportResponse {
  success: true;
  report: Report;
}

export interface SettingsResponse {
  success: true;
  settings: AppSettings;
  isDefault: boolean;
}

export type SettingsInput = Omit<AppSettings, "updatedAt">;

export type LabelKind = "fnsku" | "box";

export interface GenerateFnskuLabelsResponse {
  success: true;
  fnskuLabelsR2Key: string;
}

export interface ApiClient {
  apiFetch: <T>(path: string, init?: RequestInit) => Promise<T>;
  getMe: () => Promise<MeResponse>;
  listPlans: (filters?: ListPlansFilters) => Promise<PlansResponse>;
  getPlan: (id: string) => Promise<PlanResponse>;
  getPlanShipments: (id: string) => Promise<ShipmentsResponse>;
  getDashboard: () => Promise<DashboardResponse>;
  getLeadTime: () => Promise<LeadTimeResponse>;
  createDraftPlan: (input: CreatePlanInput) => Promise<PlanResponse>;
  cancelPlan: (id: string, input: CancelPlanInput) => Promise<CancelPlanResponse>;
  deletePlan: (id: string) => Promise<{ success: true }>;
  // Wizard
  getItems: (id: string) => Promise<ItemsResponse>;
  setPlanItems: (id: string, items: SetPlanItemInput[]) => Promise<ItemsResponse>;
  getInventory: (marketplaceId?: string) => Promise<InventoryResponse>;
  syncInventory: (
    sellerId: string,
    marketplaceId?: string
  ) => Promise<SyncInventoryResponse>;
  setInventoryFavorite: (
    sellerSku: string,
    favorite: boolean,
    marketplaceId?: string
  ) => Promise<FavoriteResponse>;
  getPrepDetails: (id: string) => Promise<PrepDetailsResponse>;
  /**
   * Enfileira o listPrepDetails do plano. `scope` "MISSING" consulta só os SKUs
   * ainda sem prep details — usado no sync automático da Etapa 1, que não pode
   * desconfirmar SKUs já resolvidos (o backend zera `confirmedAt` ao regravar).
   * "ALL" (padrão) é o "Atualizar da Amazon" manual.
   */
  refreshPrepDetails: (
    id: string,
    scope?: "ALL" | "MISSING"
  ) => Promise<EnqueuedResponse>;
  confirmPrep: (id: string, msku: string) => Promise<PrepDetailsConfirmResponse>;
  /**
   * Resolve a classificação de preparo na Amazon (setPrepDetails, assíncrono).
   * Usado quando o listPrepDetails devolve prepCategory UNKNOWN: sem registrar a
   * classificação, o createInboundPlan falha com FBA_INB_0182. A confirmação do
   * item acontece na reconciliação do backend — o chamador faz poll de
   * getPrepDetails até `confirmedAt`.
   */
  submitPrepDetails: (id: string, msku: string) => Promise<EnqueuedResponse>;
  getPackingOptions: (id: string) => Promise<PackingOptionsResponse>;
  getPackingGroups: (id: string) => Promise<PackingGroupsResponse>;
  getPlacementOptions: (id: string) => Promise<PlacementOptionsResponse>;
  getTransportationOptions: (id: string) => Promise<TransportationOptionsResponse>;
  getDeliveryWindow: (id: string, shipmentId: string) => Promise<DeliveryWindowResponse>;
  putBoxConfig: (id: string, input: BoxConfigInput) => Promise<PutBoxConfigResponse>;
  getBoxConfig: (id: string) => Promise<BoxConfigDetailsResponse>;
  startWorkflow: (id: string) => Promise<WorkflowStartResponse>;
  /**
   * Reinicia um plano que falhou (volta a rascunho) para o operador corrigir os
   * produtos do Passo 1 — a saída do beco sem saída "crie um novo plano".
   */
  restartWorkflow: (id: string) => Promise<{ success: true }>;
  /**
   * Cria um NOVO envio copiando os produtos deste plano, podendo excluir os SKUs
   * que a Amazon recusou. É a saída quando o plano já foi registrado na Amazon e
   * por isso não pode ser reiniciado.
   */
  clonePlan: (
    id: string,
    excludeMskus?: string[]
  ) => Promise<{ success: true; planId: string; copiedItemCount: number }>;
  getWorkflowStatus: (id: string) => Promise<WorkflowStatusResponse>;
  confirmPackingOption: (id: string, packingOptionId: string) => Promise<WorkflowEventResponse>;
  submitBoxConfigEvent: (id: string, boxConfigId: string) => Promise<WorkflowEventResponse>;
  selectPlacement: (
    id: string,
    placementOptionId: string,
    readyToShipDate?: string
  ) => Promise<WorkflowEventResponse>;
  selectTransportation: (
    id: string,
    selections: TransportationSelection[],
    adminOverride?: boolean
  ) => Promise<WorkflowEventResponse>;
  confirmDeliveryWindow: (id: string, selections: DeliveryWindowSelection[]) => Promise<WorkflowEventResponse>;
  downloadLabel: (
    id: string,
    kind: LabelKind,
    shipmentId?: string
  ) => Promise<Blob>;
  generateFnskuLabels: (id: string) => Promise<GenerateFnskuLabelsResponse>;
  // Cadastro › Usuários (§7) — ADMIN-only no backend
  listUsers: () => Promise<UsersResponse>;
  createUser: (input: CreateUserInput) => Promise<UserResponse>;
  deleteUser: (email: string) => Promise<{ success: true }>;
  // Fase F — §8 (mutações são ADMIN-only no backend; o frontend revalida pelo papel)
  listSuppliers: () => Promise<SuppliersResponse>;
  createSupplier: (input: SupplierInput) => Promise<SupplierResponse>;
  updateSupplier: (id: string, input: SupplierInput) => Promise<SupplierResponse>;
  deleteSupplier: (id: string) => Promise<{ success: true }>;
  listPrepCenters: () => Promise<PrepCentersResponse>;
  createPrepCenter: (input: PrepCenterInput) => Promise<PrepCenterResponse>;
  updatePrepCenter: (id: string, input: PrepCenterInput) => Promise<PrepCenterResponse>;
  deletePrepCenter: (id: string) => Promise<{ success: true }>;
  listReports: (period?: string) => Promise<ReportsResponse>;
  generateReport: (period: string) => Promise<ReportResponse>;
  downloadReport: (id: string) => Promise<Blob>;
  getSettings: () => Promise<SettingsResponse>;
  putSettings: (input: SettingsInput) => Promise<SettingsResponse>;
}

/** Tarifa/desconto → soma de valores monetários (mesma moeda assumida). */
export function sumIncentives(incentives: readonly Incentive[]): number {
  return incentives.reduce(
    (total, inc) => total + (inc.value !== null ? inc.value.amount : 0),
    0
  );
}

export interface ApiClientOptions {
  getToken?: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  baseUrl?: string;
}

function defaultBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  return fromEnv !== undefined && fromEnv.length > 0 ? fromEnv : "/api/v1";
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const getToken = options.getToken ?? (async (): Promise<string | null> => null);
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? defaultBaseUrl();

  async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await getToken();
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body !== undefined && init.body !== null) {
      headers.set("Content-Type", "application/json");
    }
    if (token !== null) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetchImpl(`${baseUrl}${path}`, { ...init, headers });
    const body = await parseJson(response);

    if (!response.ok || (isRecord(body) && body.success === false)) {
      throw toApiError(response.status, body);
    }

    return body as T;
  }

  const pid = (id: string): string => encodeURIComponent(id);

  function sendEvent(
    id: string,
    body: { type: string; payload: Record<string, unknown> }
  ): Promise<WorkflowEventResponse> {
    return apiFetch<WorkflowEventResponse>(
      `/inbound-plans/${pid(id)}/workflow/event`,
      { method: "POST", body: JSON.stringify(body) }
    );
  }

  async function downloadBlob(path: string): Promise<Blob> {
    const token = await getToken();
    const headers = new Headers();
    if (token !== null) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const response = await fetchImpl(`${baseUrl}${path}`, { headers });
    if (!response.ok) {
      throw toApiError(response.status, await parseJson(response));
    }
    return response.blob();
  }

  /**
   * PDF de etiquetas. `shipmentId` (só para `box`) baixa a etiqueta DAQUELE
   * envio: a Amazon emite um PDF por remessa e um plano dividido tem vários —
   * sem o parâmetro o backend devolve a do 1º envio (compatibilidade).
   */
  function downloadLabel(
    id: string,
    kind: LabelKind,
    shipmentId?: string
  ): Promise<Blob> {
    const query =
      shipmentId !== undefined ? `?shipmentId=${encodeURIComponent(shipmentId)}` : "";
    return downloadBlob(`/inbound-plans/${pid(id)}/labels/${kind}${query}`);
  }

  function generateFnskuLabels(id: string): Promise<GenerateFnskuLabelsResponse> {
    return apiFetch<GenerateFnskuLabelsResponse>(
      `/inbound-plans/${pid(id)}/labels/fnsku/generate`,
      { method: "POST" }
    );
  }

  return {
    apiFetch,
    getMe: () => apiFetch<MeResponse>("/me"),
    listPlans: (filters) =>
      apiFetch<PlansResponse>(`/inbound-plans${buildPlansQuery(filters)}`),
    getPlan: (id) => apiFetch<PlanResponse>(`/inbound-plans/${encodeURIComponent(id)}`),
    getPlanShipments: (id) =>
      apiFetch<ShipmentsResponse>(
        `/inbound-plans/${encodeURIComponent(id)}/shipments`
      ),
    getDashboard: () => apiFetch<DashboardResponse>("/dashboard"),
    getLeadTime: () => apiFetch<LeadTimeResponse>("/lead-time"),
    createDraftPlan: (input) =>
      apiFetch<PlanResponse>("/inbound-plans", {
        method: "POST",
        body: JSON.stringify(input)
      }),
    cancelPlan: (id, input) =>
      apiFetch<CancelPlanResponse>(`/inbound-plans/${pid(id)}/cancel`, {
        method: "POST",
        body: JSON.stringify(input)
      }),
    deletePlan: (id) =>
      apiFetch<{ success: true }>(`/inbound-plans/${pid(id)}`, {
        method: "DELETE"
      }),
    // ───────────────────── Wizard Criar envios (§6) ─────────────────────
    getItems: (id) => apiFetch<ItemsResponse>(`/inbound-plans/${pid(id)}/items`),
    setPlanItems: (id, items) =>
      apiFetch<ItemsResponse>(`/inbound-plans/${pid(id)}/items`, {
        method: "PUT",
        body: JSON.stringify({ items })
      }),
    getInventory: (marketplaceId) =>
      apiFetch<InventoryResponse>(
        marketplaceId !== undefined
          ? `/inventory?marketplaceId=${encodeURIComponent(marketplaceId)}`
          : "/inventory"
      ),
    syncInventory: (sellerId, marketplaceId) =>
      apiFetch<SyncInventoryResponse>("/inventory/sync", {
        method: "POST",
        body: JSON.stringify(
          marketplaceId !== undefined ? { sellerId, marketplaceId } : { sellerId }
        )
      }),
    setInventoryFavorite: (sellerSku, favorite, marketplaceId) =>
      apiFetch<FavoriteResponse>("/inventory/favorite", {
        method: "PUT",
        body: JSON.stringify(
          marketplaceId !== undefined
            ? { sellerSku, favorite, marketplaceId }
            : { sellerSku, favorite }
        )
      }),
    getPrepDetails: (id) =>
      apiFetch<PrepDetailsResponse>(`/inbound-plans/${pid(id)}/prep-details`),
    refreshPrepDetails: (id, scope) =>
      apiFetch<EnqueuedResponse>(`/inbound-plans/${pid(id)}/prep-details/refresh`, {
        method: "POST",
        body: JSON.stringify(scope !== undefined ? { scope } : {})
      }),
    confirmPrep: (id, msku) =>
      apiFetch<PrepDetailsConfirmResponse>(
        `/inbound-plans/${pid(id)}/prep-details/${pid(msku)}/confirm`,
        { method: "POST" }
      ),
    submitPrepDetails: (id, msku) =>
      apiFetch<EnqueuedResponse>(
        `/inbound-plans/${pid(id)}/prep-details/${pid(msku)}/set`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({})
        }
      ),
    getPackingOptions: (id) =>
      apiFetch<PackingOptionsResponse>(`/inbound-plans/${pid(id)}/options/packing`),
    getPackingGroups: (id) =>
      apiFetch<PackingGroupsResponse>(`/inbound-plans/${pid(id)}/packing-groups`),
    getPlacementOptions: (id) =>
      apiFetch<PlacementOptionsResponse>(
        `/inbound-plans/${pid(id)}/options/placement`
      ),
    getTransportationOptions: (id) =>
      apiFetch<TransportationOptionsResponse>(
        `/inbound-plans/${pid(id)}/options/transportation`
      ),
    getDeliveryWindow: (id, shipmentId) =>
      apiFetch<DeliveryWindowResponse>(
        `/inbound-plans/${pid(id)}/options/delivery-window?shipmentId=${pid(shipmentId)}`
      ),
    putBoxConfig: (id, input) =>
      apiFetch<PutBoxConfigResponse>(`/inbound-plans/${pid(id)}/box-config`, {
        method: "PUT",
        body: JSON.stringify(input)
      }),
    getBoxConfig: (id) =>
      apiFetch<BoxConfigDetailsResponse>(`/inbound-plans/${pid(id)}/box-config`),
    startWorkflow: (id) =>
      apiFetch<WorkflowStartResponse>(`/inbound-plans/${pid(id)}/workflow/start`, {
        method: "POST"
      }),
    restartWorkflow: (id) =>
      apiFetch<{ success: true }>(`/inbound-plans/${pid(id)}/workflow/restart`, {
        method: "POST"
      }),
    clonePlan: (id, excludeMskus) =>
      apiFetch<{ success: true; planId: string; copiedItemCount: number }>(
        `/inbound-plans/${pid(id)}/clone`,
        {
          method: "POST",
          body: JSON.stringify(
            excludeMskus === undefined ? {} : { excludeMskus }
          )
        }
      ),
    getWorkflowStatus: (id) =>
      apiFetch<WorkflowStatusResponse>(`/inbound-plans/${pid(id)}/workflow/status`),
    confirmPackingOption: (id, packingOptionId) =>
      sendEvent(id, {
        type: "packing-option-confirmed",
        payload: { planId: id, packingOptionId }
      }),
    submitBoxConfigEvent: (id, boxConfigId) =>
      sendEvent(id, {
        type: "box-config-submitted",
        payload: { planId: id, boxConfigId }
      }),
    selectPlacement: (id, placementOptionId, readyToShipDate) =>
      sendEvent(id, {
        type: "placement-option-selected",
        payload: {
          planId: id,
          placementOptionId,
          ...(readyToShipDate !== undefined ? { readyToShipDate } : {})
        }
      }),
    selectTransportation: (id, selections, adminOverride) =>
      sendEvent(id, {
        type: "transportation-option-selected",
        payload: {
          planId: id,
          selections,
          ...(adminOverride === true ? { adminOverride: true } : {})
        }
      }),
    confirmDeliveryWindow: (id, selections) =>
      sendEvent(id, {
        type: "delivery-window-confirmed",
        payload: { planId: id, selections }
      }),
    downloadLabel,
    generateFnskuLabels,
    // ───────────────────── Cadastro › Usuários (§7) ─────────────────────
    listUsers: () => apiFetch<UsersResponse>("/users"),
    createUser: (input) =>
      apiFetch<UserResponse>("/users", {
        method: "POST",
        body: JSON.stringify(input)
      }),
    deleteUser: (email) =>
      apiFetch<{ success: true }>(`/users/${pid(email)}`, {
        method: "DELETE"
      }),
    // ───────────────────── Fase F — §8 (cadastros/relatório) ─────────────────
    listSuppliers: () => apiFetch<SuppliersResponse>("/suppliers"),
    createSupplier: (input) =>
      apiFetch<SupplierResponse>("/suppliers", {
        method: "POST",
        body: JSON.stringify(input)
      }),
    updateSupplier: (id, input) =>
      apiFetch<SupplierResponse>(`/suppliers/${pid(id)}`, {
        method: "PUT",
        body: JSON.stringify(input)
      }),
    deleteSupplier: (id) =>
      apiFetch<{ success: true }>(`/suppliers/${pid(id)}`, { method: "DELETE" }),
    listPrepCenters: () => apiFetch<PrepCentersResponse>("/prep-centers"),
    createPrepCenter: (input) =>
      apiFetch<PrepCenterResponse>("/prep-centers", {
        method: "POST",
        body: JSON.stringify(input)
      }),
    updatePrepCenter: (id, input) =>
      apiFetch<PrepCenterResponse>(`/prep-centers/${pid(id)}`, {
        method: "PUT",
        body: JSON.stringify(input)
      }),
    deletePrepCenter: (id) =>
      apiFetch<{ success: true }>(`/prep-centers/${pid(id)}`, { method: "DELETE" }),
    listReports: (period) =>
      apiFetch<ReportsResponse>(
        period !== undefined
          ? `/reports?period=${encodeURIComponent(period)}`
          : "/reports"
      ),
    generateReport: (period) =>
      apiFetch<ReportResponse>("/reports/generate", {
        method: "POST",
        body: JSON.stringify({ period })
      }),
    downloadReport: (id) => downloadBlob(`/reports/${pid(id)}/download`),
    getSettings: () => apiFetch<SettingsResponse>("/settings"),
    putSettings: (input) =>
      apiFetch<SettingsResponse>("/settings", {
        method: "PUT",
        body: JSON.stringify(input)
      })
  };
}

/** Monta a query string de listPlans só com os filtros definidos. */
function buildPlansQuery(filters?: ListPlansFilters): string {
  if (filters === undefined) {
    return "";
  }
  const params = new URLSearchParams();
  if (filters.sellerId !== undefined) {
    params.set("sellerId", filters.sellerId);
  }
  if (filters.marketplaceId !== undefined) {
    params.set("marketplaceId", filters.marketplaceId);
  }
  if (filters.status !== undefined) {
    params.set("status", filters.status);
  }
  if (filters.limit !== undefined) {
    params.set("limit", String(filters.limit));
  }
  if (filters.offset !== undefined) {
    params.set("offset", String(filters.offset));
  }
  const query = params.toString();
  return query.length > 0 ? `?${query}` : "";
}

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function toApiError(status: number, body: unknown): ApiFetchError {
  if (isRecord(body) && isRecord(body.error)) {
    const code =
      typeof body.error.code === "string" ? body.error.code : "API_ERROR";
    const message =
      typeof body.error.message === "string"
        ? body.error.message
        : "Falha na requisição.";
    const fields = extractFields(body.error.fields);
    return new ApiFetchError(message, code, status, fields);
  }

  return new ApiFetchError(
    `Falha na requisição (HTTP ${status}).`,
    "API_ERROR",
    status
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function extractFields(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) {
    return null;
  }
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(value)) {
    if (typeof val === "string") {
      out[key] = val;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}
